import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Disclaimer } from '@/components/Disclaimer';

export const MacroTargetsStep = ({ formData, setFormData }) => (
  <div className="max-w-sm mx-auto space-y-6">
    <p className="text-sm text-[#1A3A4A] dark:text-[#F0EDE8] leading-relaxed font-medium text-center">
      Based on your profile, here are your recommended daily nutrition targets. They're calculated specifically for you and your goal. You can adjust them anytime in Settings.
    </p>
    <div className="text-center py-2">
      <div className="text-5xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8] animate-count-up">{formData.calories_target}</div>
      <div className="text-base text-[#7A7A72] dark:text-[#A1A1AA] mt-1">calories per day</div>
      <p className="text-xs text-[#6B9E8A] dark:text-[#6B9E8A] mt-1 font-medium">
        {formData.goal === 'lose_weight' && 'Your daily energy budget to lose weight safely'}
        {formData.goal === 'gain_muscle' && 'Your daily energy budget to build muscle'}
        {formData.goal === 'maintain' && 'Your daily energy budget to maintain your weight'}
        {formData.goal === 'improve_nutrition' && 'Your daily energy budget for optimal nutrition'}
        {!formData.goal && 'Your daily energy budget \u2014 stay near this number to reach your goal'}
      </p>
    </div>
    <div className="space-y-5">
      {[
        { key: 'protein_target', label: 'Protein', color: '#4A90D9', unit: 'g', min: 50, max: 300,
          helper: 'Builds & repairs muscle. Think: meat, eggs, Greek yogurt, protein shakes.' },
        { key: 'carbs_target', label: 'Carbs', color: '#E5A84B', unit: 'g', min: 50, max: 500,
          helper: "Your body's main energy source. Think: bread, rice, pasta, fruit, oats." },
        { key: 'fat_target', label: 'Fat', color: '#D4736A', unit: 'g', min: 20, max: 200,
          helper: 'Essential for hormones & nutrient absorption. Think: oils, nuts, avocado, cheese.' },
      ].map(m => (
        <div key={m.key}>
          <div className="flex justify-between mb-1">
            <span className="text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">{m.label}</span>
            <span className="text-sm font-bold" style={{ color: m.color }}>{formData[m.key]}{m.unit}</span>
          </div>
          <p className="text-[11px] text-[#7A7A72] dark:text-[#A1A1AA] mb-2 leading-snug">{m.helper}</p>
          <Slider
            data-testid={`slider-${m.key}`}
            value={[formData[m.key]]}
            onValueChange={([v]) => {
              const newData = { ...formData, [m.key]: v };
              const newCals = newData.protein_target * 4 + newData.carbs_target * 4 + newData.fat_target * 9;
              setFormData({ ...newData, calories_target: Math.round(newCals) });
            }}
            min={m.min}
            max={m.max}
            step={5}
            className="w-full"
          />
        </div>
      ))}
    </div>
    <details className="rounded-[16px] bg-white/[0.85] dark:bg-[#141C22]/[0.85] border border-white/30 dark:border-white/10 backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] overflow-hidden" data-testid="calculation-details">
      <summary className="px-4 py-3 text-xs font-medium text-[#6B9E8A] cursor-pointer hover:bg-white/30 dark:hover:bg-white/5 transition select-none">
        How we calculated this
      </summary>
      <div className="px-4 pb-3 text-xs text-[#7A7A72] dark:text-[#A1A1AA] leading-relaxed space-y-1">
        <p>Based on your age ({formData.age}), height, weight, and activity level ({formData.activity_level}), your estimated maintenance calories are around {formData.calories_target + (formData.goal === 'lose_weight' ? 500 : formData.goal === 'gain_muscle' ? -300 : 0)} cal/day.</p>
        {formData.goal === 'lose_weight' && <p>For your goal of losing weight, we've set a ~500 cal deficit, which targets ~1 lb/week loss.</p>}
        {formData.goal === 'gain_muscle' && <p>For your goal of building muscle, we've added a ~300 cal surplus to fuel growth.</p>}
        {formData.goal === 'maintain' && <p>For maintenance, your targets match your estimated expenditure.</p>}
        <p>Your protein target of {formData.protein_target}g is based on ~0.8g per pound of body weight \u2014 research shows this is optimal for {formData.goal === 'lose_weight' ? 'preserving muscle during weight loss' : formData.goal === 'gain_muscle' ? 'maximizing muscle growth' : 'overall health'}.</p>
      </div>
    </details>
  </div>
);

export const ConsentStep = ({ formData }) => (
  <div className="max-w-md mx-auto space-y-8">
    <div className="rounded-[16px] bg-white/85 dark:bg-[#141C22]/85 backdrop-blur-sm border border-white/30 dark:border-white/10 p-5">
      <h3 className="font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] mb-3">AI Analysis Consent</h3>
      <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] leading-relaxed">
        Your meal photos are analyzed by Nuri AI to identify food items and estimate nutritional content.
        Photos are processed securely and not used for training AI models. You can delete your data at any time.
      </p>
    </div>
    <div className="rounded-[16px] bg-white/85 dark:bg-[#141C22]/85 backdrop-blur-sm border border-white/30 dark:border-white/10 p-5">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">Free Plan</h3>
          <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mt-1">Photo logging + basic tracking</p>
        </div>
        <span className="text-2xl font-bold text-[#6B9E8A]">$0</span>
      </div>
      <div className="border-t border-[#1A3A4A]/10 dark:border-white/10 my-4" />
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">Pro Plan</h3>
          <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mt-1">Conversational accuracy + all features</p>
        </div>
        <span className="text-2xl font-bold text-[#C67A5C]">$14.99<span className="text-sm font-normal text-[#7A7A72]">/mo</span></span>
      </div>
    </div>
    <Disclaimer variant="card" className="mt-2" />
    <p className="text-center text-sm text-[#7A7A72] dark:text-[#A1A1AA] mt-4">
      Start with free. Upgrade anytime.
    </p>
  </div>
);
